import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import { SiteProvider } from "@/context/SiteContext";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
const About = lazy(() => import("@/pages/About"));
const ServiceTimes = lazy(() => import("@/pages/ServiceTimes"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Contact = lazy(() => import("@/pages/Contact"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const WSF = lazy(() => import("@/pages/WSF"));
const Testimonies = lazy(() => import("@/pages/Testimonies"));
const CovenantHour = lazy(() => import("@/pages/CovenantHour"));
const Transport = lazy(() => import("@/pages/Transport"));
const Parking = lazy(() => import("@/pages/Parking"));
const ChildrensChurch = lazy(() => import("@/pages/ChildrensChurch"));
const KingdomCare = lazy(() => import("@/pages/KingdomCare"));
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));

const PageFallback = () => (
  <div className="min-h-screen grid place-items-center bg-sec-2">
    <Loader2 className="w-8 h-8 animate-spin text-chapel-red" />
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App min-h-screen bg-sec-2 text-white">
      <BrowserRouter>
        <SiteProvider>
          <AuthProvider>
            <ScrollToTop />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/service-times" element={<ServiceTimes />} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/visit" element={<Contact />} />
                  <Route path="/events" element={<Navigate to="/calendar" replace />} />
                  <Route path="/calendar" element={<Calendar />} />
                  <Route path="/wsf" element={<WSF />} />
                  <Route path="/testimonies" element={<Testimonies />} />
                  <Route path="/covenant-hour" element={<CovenantHour />} />
                  <Route path="/transport" element={<Transport />} />
                  <Route path="/parking" element={<Parking />} />
                  <Route path="/childrens-church" element={<ChildrensChurch />} />
                  <Route path="/kingdom-care" element={<KingdomCare />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </SiteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
